import uniq from 'lodash/uniq';
import {
    initialSymbolTilesStreamState,
    initialSymbolTilesSymbolBarItem,
    initialSymbolTilesSymbolState,
    SymbolStateDictionary,
    SymbolTilesStreamState,
    SymbolTilesSymbolBarItem,
    SymbolTilesSymbolState,
} from 'widgets/symbolTile/store/data/SymbolTilesStreamState';
import {
    SymbolTilesStreamAction,
    SymbolTilesStreamActionType,
} from 'widgets/symbolTile/store/data/actions';
import {
    SymbolTilesMessage,
    SymbolTilesSymbolElement,
    SymbolTilesSymbolPriceItem,
} from 'communication/types/symbolTiles/SymbolTilesElement';
import isNil from 'lodash/isNil';
import { ensureInvalidStreamMessage } from 'widgets/store/stream/reducer';
import omit from 'lodash/omit';

export function symbolTilesStreamStateReducer(
    state = initialSymbolTilesStreamState,
    action: SymbolTilesStreamAction
) {
    switch (action.type) {
        case SymbolTilesStreamActionType.WATCH_SYMBOL_TILES_STREAM:
            return { ...state, streamKey: action.payload?.streamKey };
        case SymbolTilesStreamActionType.CLEAR_SYMBOL_TILES_STREAM: {
            return {
                ...initialSymbolTilesStreamState,
            };
        }
        case SymbolTilesStreamActionType.REMOVE_SYMBOL_TILES_STREAM: {
            const nextState = { ...state };
            nextState.data = omit(state.data, action.payload!.symbol);
            return nextState;
        }
        case SymbolTilesStreamActionType.HANDLE_SYMBOL_TILES_STREAM_ERROR: {
            return {
                ...state,
                messages: ensureInvalidStreamMessage(
                    state.messages,
                    action.payload!.error
                ),
            };
        }
        case SymbolTilesStreamActionType.HANDLE_SYMBOL_TILES_STREAM_MESSAGE_BATCH: {
            let nextState = { ...state };
            for (const message of action.payload!.messages) {
                nextState = reduceSymbolTilesMessage(nextState, message);
            }
            return nextState;
        }
    }
    return state;
}

function reduceSymbolTilesMessage(
    state: SymbolTilesStreamState,
    message: SymbolTilesMessage
): SymbolTilesStreamState {
    const nextState: SymbolTilesStreamState = { ...state };

    let nextSymbolsState: SymbolStateDictionary = { ...state.data };

    if (message.msg) {
        nextState.messages = [...nextState.messages, { message: message.msg }];
    }

    if (!isNil(message.imdsAuthorized) && !message.imdsAuthorized) {
        nextState.messages = [
            ...nextState.messages,
            {
                message: `Market data access blocked due to possible concurrent use from multiple machines`,
            },
        ];
    }

    if (message.data) {
        for (const symbolData of message.data) {
            nextSymbolsState = reduceSymbolDataState(
                nextSymbolsState,
                symbolData
            );
        }
    }

    const symbols = uniq([
        ...Object.keys(nextSymbolsState),
        ...(message.missingSyms ?? []),
        ...(message.noMktPerm ?? []),
        ...(message.noMktData ?? []),
        ...(message.excludedSyms ?? []),
    ]);

    for (const symbol of symbols) {
        nextSymbolsState = reduceSymbolMessages(
            nextSymbolsState,
            message,
            symbol
        );
    }

    nextState.data = nextSymbolsState;

    return nextState;
}

function reduceSymbolDataState(
    state: SymbolStateDictionary,
    symbolData: SymbolTilesSymbolElement
): SymbolStateDictionary {
    const nextSymbolState = {
        ...(state[symbolData.symbol] ?? { ...initialSymbolTilesSymbolState }),
    };

    if (!isNil(symbolData.pnc)) {
        nextSymbolState.pnc = symbolData.pnc;
    }
    if (!isNil(symbolData.curr)) {
        nextSymbolState.currency = symbolData.curr;
    }
    if (!isNil(symbolData.tickSize)) {
        nextSymbolState.tickSize = symbolData.tickSize;
    }
    if (!isNil(symbolData.bid)) {
        nextSymbolState.bid = symbolData.bid;
    }
    if (!isNil(symbolData.ask)) {
        nextSymbolState.ask = symbolData.ask;
    }
    if (!isNil(symbolData.lp)) {
        nextSymbolState.lastTradePrice = symbolData.lp;
    }
    if (!isNil(symbolData.chg)) {
        nextSymbolState.lastTradePriceChange = symbolData.chg;
    }
    if (!isNil(symbolData.pctChg)) {
        nextSymbolState.lastTradePriceChangePercent = symbolData.pctChg;
    }

    if (symbolData.bars) {
        nextSymbolState.bars = reduceSymbolBars(
            nextSymbolState.bars,
            symbolData.bars
        );
    }

    return { ...state, [symbolData.symbol]: nextSymbolState };
}

function reduceSymbolMessages(
    state: SymbolStateDictionary,
    message: SymbolTilesMessage,
    symbol: string
): SymbolStateDictionary {
    let nextSymbolState = state[symbol] ?? { ...initialSymbolTilesSymbolState };

    if (
        (message.noMktData ?? []).indexOf(symbol) > -1 ||
        (message.excludedSyms ?? []).indexOf(symbol) > -1
    ) {
        nextSymbolState = reduceSymbolMessage(
            nextSymbolState,
            `Market data access is globally excluded for "${symbol}"`
        );
    }
    if ((message.missingSyms ?? []).indexOf(symbol) > -1) {
        nextSymbolState = reduceSymbolMessage(
            nextSymbolState,
            `Symbol "${symbol}" is not recognised`
        );
    }
    if ((message.noMktPerm ?? []).indexOf(symbol) > -1) {
        nextSymbolState = reduceSymbolMessage(
            nextSymbolState,
            `You do not have market permission for "${symbol}"`
        );
    }

    return { ...state, [symbol]: nextSymbolState };
}

function reduceSymbolMessage(
    state: SymbolTilesSymbolState,
    message: string
): SymbolTilesSymbolState {
    const nextSymbolState = { ...state };
    if (!nextSymbolState.messages.find((m) => m.message === message)) {
        nextSymbolState.messages = [...nextSymbolState.messages, { message }];
    }
    return nextSymbolState;
}

function reduceSymbolBars(
    state: SymbolTilesSymbolBarItem[],
    items: SymbolTilesSymbolPriceItem[]
) {
    const nextState = [...state];

    for (const item of items) {
        let existing = nextState.find((i) => i._i === item._i);
        if (!existing) {
            existing = { ...initialSymbolTilesSymbolBarItem, _i: item._i };
            nextState.push(existing);
        }
        nextState[nextState.indexOf(existing)] = {
            ...existing,
            closePrice: isNil(item.c) ? existing.closePrice : item.c,
            percentChange: isNil(item.ch) ? existing.percentChange : item.ch,
            high: isNil(item.h) ? existing.high : item.h,
            low: isNil(item.l) ? existing.low : item.l,
        };
    }

    return nextState;
}
